import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { Text } from '@components/Text/Text';

import type { DropdownItems } from './DropdownMenu';

let lastId = 0;
const generateId = (): string => {
  lastId += 1;
  return `dropdown-section-${lastId}`;
};

export interface DropdownSectionProps {
  /**
   * List of DropdownMenu.Item components.
   */
  children: DropdownItems;
  /**
   * Title of the section
   */
  title: string;
}

const StyledText = styled(Text)`
  display: block;
  height: ${tokens.spacer.x4};
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2} ${tokens.spacer.x0pt5};
`;

/**
 * Use DropdownMenu.Section to group elements inside DropdownMenu.
 *
 * {@link https://zeroheight.com/76c439891/p/96bb9e-dropdown-menu/b/72fb4d Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-dropdownmenu Code documentation}
 *
 * @example
 * <DropdownMenu.Section title="Section title">
 *   <DropdownMenu.Item>Item</DropdownMenu.Item>
 * </DropdownMenu.Section>
 */
export const DropdownSection = ({
  title,
  children,
}: DropdownSectionProps): JSX.Element => {
  const sectionId = generateId();

  return (
    <li role="presentation">
      <StyledText
        textType="caption"
        textColor="text_light"
        as="span"
        id={sectionId}
        aria-hidden
      >
        {title}
      </StyledText>
      <ul role="group" aria-labelledby={sectionId}>
        {children}
      </ul>
    </li>
  );
};

DropdownSection.displayName = 'DropdownSection';
